import React, { useEffect, type FC } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { useLocation } from 'react-router-dom';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	ExpandableMenuItem,
	ExpandableMenuItemTrigger,
	ExpandableMenuItemContent,
} from '@atlaskit/side-nav-items/expandable-menu-item';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useRouteDataRef, useRouteUrl } from '@confluence/route-manager/entry-points/RouteState.ts';
import { GLOBAL_PAGE_FORGE } from '@confluence/named-routes/entry-points/routes.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';
import { useWebItemLocation } from '@confluence/web-item-location/entry-points/useWebItemLocation.ts';
import { useExtensionList } from '@confluence/forge-ui/entry-points/useExtensionList.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';

import { AppsIconComponent } from '../../GlobalNavigationIcons.tsx';
import { i18n } from '../../globalNavigationTranslations.ts';
import type { GlobalItemProps } from '../globalItemProps.ts';
import { useGlobalItemVisibility } from '../useGlobalItemVisibility.ts';

import { AppsMenuContent } from './AppsMenuContent.tsx';
import { AppsMoreActionsMenu } from './AppsMoreActionsMenu.tsx';
import { getGlobalAppsParams } from './getGlobalAppsParams.ts';

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(
			await import(
				/* webpackChunkName: "loadable-analyticsCallbacks" */ '../../analyticsCallbacks.ts'
			)
		).fireAppsClickedAnalytics,
);

export const AppsItem: FC<GlobalItemProps> = ({ isHidden, peekingId, setPeekingId }) => {
	const { edition, accessStatus } = useSessionData();
	const isAdmin = accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS;

	const routeDataRef = useRouteDataRef();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireClickAnalytics = useLazyClickAnalytics(createAnalyticsEvent, routeDataRef);

	const { webItemParams, forgeExtensionParams } = getGlobalAppsParams({
		edition,
		accessStatus,
	});

	const {
		webItems,
		loading: webItemsLoading,
		error: webItemsError,
	} = useWebItemLocation(webItemParams);

	const {
		extensions,
		loading: extensionsLoading,
		error: extensionsError,
	} = useExtensionList(forgeExtensionParams);

	const webItemUrls = webItems.map((webItem) => (webItem.url || '').split('?')[0]);
	const extensionsUrls = extensions.map((extension) =>
		GLOBAL_PAGE_FORGE.toUrl({
			appId: extension.appId,
			envId: extension.environmentId,
			forgeManifestRoute: extension.properties.route,
		}),
	);

	const location = useLocation();
	const isOnAppsRoute = useRouteUrl({
		selector: (routeUrl: string | undefined) => {
			const path = routeUrl || location?.pathname;
			return (
				webItemUrls.some((appUrl) => path.includes(appUrl)) ||
				extensionsUrls.some((appUrl) => path.includes(appUrl)) ||
				path.includes(`${CONTEXT_PATH}/marketplace`)
			);
		},
	});

	const loading = webItemsLoading || extensionsLoading;

	const shouldHide = useGlobalItemVisibility(
		'apps',
		isOnAppsRoute,
		isHidden,
		peekingId,
		setPeekingId,
	);

	if (shouldHide) {
		return null;
	}

	const onClick = (
		_: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
		__: UIAnalyticsEvent,
		analyticsAttributes: { isExpanded: boolean },
	) => {
		const { isExpanded } = analyticsAttributes;
		void fireClickAnalytics(isExpanded);
	};

	return (
		<>
			<ExpandableMenuItem isDefaultExpanded={isOnAppsRoute}>
				<ExpandableMenuItemTrigger
					elemBefore={AppsIconComponent}
					actionsOnHover={isAdmin && <AppsMoreActionsMenu />}
					onClick={onClick}
				>
					<FormattedMessage {...i18n.apps} />
				</ExpandableMenuItemTrigger>
				<ExpandableMenuItemContent>
					<AppsMenuContent loading={loading} webItems={webItems} extensions={extensions} />
				</ExpandableMenuItemContent>
			</ExpandableMenuItem>
			<ErrorFlag webItemsError={webItemsError} extensionsError={extensionsError} />
		</>
	);
};

const ErrorFlag = withFlags(
	({
		webItemsError,
		extensionsError,
		flags,
	}: {
		webItemsError: Error | undefined;
		extensionsError: Error | undefined;
	} & WithFlagsProps) => {
		useEffect(() => {
			if (webItemsError || extensionsError) {
				void flags.showFlag({
					title: <FormattedMessage {...errorI18n.errorTitle} />,
					description: <FormattedMessage {...errorI18n.errorDescription} />,
					type: 'error',
					close: 'auto',
				});
			}
		}, [webItemsError, extensionsError, flags]);

		return (
			<>
				{webItemsError && <ErrorDisplay error={webItemsError} />}
				{extensionsError && <ErrorDisplay error={extensionsError} />}
			</>
		);
	},
);

const errorI18n = defineMessages({
	errorTitle: {
		id: 'side-navigation.global.apps.error-title',
		defaultMessage: 'We couldn’t load apps',
		description: 'Title for the error flag when fetching apps fails',
	},
	errorDescription: {
		id: 'side-navigation.global.apps.error-description',
		defaultMessage: 'Refresh and try again.',
		description: 'Description for the error flag when fetching apps fails',
	},
});
